import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, type VerifyResult } from '../api';

const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

/**
 * The page a client, a hospital or an auditor lands on from the address printed
 * on a certificate. No account needed, and nothing here that is not on the
 * certificate they are holding.
 */
export function Verify({ program = false }: { program?: boolean } = {}) {
  const { publicId } = useParams();
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setResult(null); setError(null);
    void (async () => {
      try {
        const response = await api.get<VerifyResult & { programName?: string }>(
          program ? `/program-graduations/verify/${publicId}` : `/certificates/verify/${publicId}`);
        if (active) setResult(program ? { ...response, courseName: response.programName ?? '', courseCode: null } : response);
      } catch (e) {
        if (active) setError((e as ApiError).status === 404 ? 'notfound' : (e as ApiError).message);
      }
    })();
    return () => { active = false; };
  }, [publicId, program]);

  return (
    <div className="verify">
      <h1 className="verify__brand"><span aria-hidden>◆</span> Learnica</h1>

      {!result && !error && <p className="muted">Checking…</p>}

      {error === 'notfound' && (
        <div className="verify__card verify__card--bad">
          <h2 className="h2">No certificate with that reference</h2>
          <p>
            Nothing in our records matches <code>{publicId}</code>. Check the reference against the
            certificate — it is a long code in the form 8-4-4-4-12.
          </p>
        </div>
      )}
      {error && error !== 'notfound' && <p className="auth__error">{error}</p>}

      {result && (
        <div className={`verify__card verify__card--${result.standing === 'valid' ? 'good' : 'bad'}`}>
          <h2 className="h2">
            {result.standing === 'valid' && 'This certificate is valid'}
            {result.standing === 'expired' && 'This certificate has expired'}
            {result.standing === 'revoked' && 'This certificate has been revoked'}
          </h2>

          <dl className="verify__facts">
            <dt>Holder</dt><dd>{result.holder}</dd>
            <dt>{program ? 'Program' : 'Course'}</dt><dd>{result.courseName}{result.courseCode ? ` (${result.courseCode})` : ''}</dd>
            <dt>Issued</dt><dd>{day(result.issuedAt)}</dd>
            <dt>Valid until</dt><dd>{result.expiresAt ? day(result.expiresAt) : 'does not expire'}</dd>
            <dt>Issued by</dt><dd>{result.issuedBy}</dd>
            <dt>Reference</dt><dd className="mono">{publicId}</dd>
          </dl>

          {result.standing === 'expired' && (
            <p className="muted">
              The training was completed and recorded; the validity period set for this training has since
              passed. Ask the holder for a current certificate.
            </p>
          )}
          {result.standing === 'revoked' && (
            <p className="muted">
              This certificate was withdrawn by {result.issuedBy} and should not be relied on.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
